/**
 * Centralized HTTP client for all API calls
 * Handles errors, retries, and notifications
 */

import { setFlashMessage } from '../../helpers';

class ApiClient {
    constructor() {
        this.baseURL = '';
        this.defaultRetries = 3;
        this.defaultTimeout = 10000;
    }

    /**
     * Make a GET request
     */
    async get(url, options = {}) {
        const { params, ...restOptions } = options;
        const requestUrl = this._buildUrl(url, params);
        return this._request(requestUrl, {
            method: 'GET',
            ...restOptions
        });
    }

    /**
     * Make a POST request
     */
    async post(url, data = {}, options = {}) {
        const { headers = {}, ...restOptions } = options;
        const bodyParams = this._buildFormParams(data);
        return this._request(url, {
            method: 'POST',
            body: bodyParams.toString(),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                ...headers
            },
            ...restOptions
        });
    }

    /**
     * Make a DELETE request
     */
    async delete(url, options = {}) {
        return this._request(url, {
            method: 'DELETE',
            ...options
        });
    }

    /**
     * Internal request method with retry logic
     */
    async _request(url, options = {}) {
        const {
            method = 'GET',
            body = null,
            headers = {},
            retries = this.defaultRetries,
            timeout = this.defaultTimeout,
            showError = true,
            attempt = 1,
            params
        } = options;
        const { params: _ignoredParams, ...restOptions } = options;

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const response = await fetch(url, {
                method,
                body,
                headers,
                signal: controller.signal,
                ...restOptions
            });

            clearTimeout(timeoutId);

            const data = await response.json();

            // Check for API error response
            if (data.error) {
                if (showError) {
                    setFlashMessage('error', data.error);
                }
                throw new Error(data.error);
            }

            if (!response.ok && showError) {
                setFlashMessage('error', `Erreur ${response.status}: ${response.statusText}`);
                throw new Error(`HTTP ${response.status}`);
            }

            return data;

        } catch (error) {
            // Retry on network errors (not on user errors)
            if (attempt < retries && this._isRetryableError(error)) {
                console.warn(`Request failed (attempt ${attempt}/${retries}):`, error.message);
                await this._sleep(1000 * attempt); // Exponential backoff
                return this._request(url, { ...options, attempt: attempt + 1 });
            }

            // Log error
            console.error(`API Error [${method} ${url}]:`, error);

            if (showError && error.name !== 'AbortError') {
                setFlashMessage('error', error.message || 'Erreur réseau');
            }

            throw error;
        }
    }

    /**
     * Check if error is retryable
     */
    _isRetryableError(error) {
        // Don't retry on AbortError (timeout or manual abort)
        if (error.name === 'AbortError') return false;
        
        // Retry on network errors
        return error instanceof TypeError;
    }

    /**
     * Sleep utility for retries
     */
    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    _buildUrl(url, params = null) {
        if (!params) {
            return url;
        }
        const requestUrl = new URL(url, window.location.origin);
        const searchParams = new URLSearchParams(requestUrl.search);
        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (Array.isArray(value)) {
                const arrayKey = key === 'groups' ? `${key}[]` : key;
                value.forEach((item) => searchParams.append(arrayKey, item));
                return;
            }
            if (value && typeof value === 'object') {
                searchParams.set(key, JSON.stringify(value));
                return;
            }
            if (value === null || typeof value === 'undefined') {
                return;
            }
            searchParams.set(key, value);
        });
        requestUrl.search = searchParams.toString();
        return requestUrl.toString();
    }

    _appendFormParam(params, key, value) {
        const normalizedValue = value === null || typeof value === 'undefined' ? '' : value;
        params.append(key, normalizedValue);
    }

    _buildFormParams(data, params = new URLSearchParams(), parentKey) {
        if (data === null || typeof data === 'undefined') {
            if (parentKey) {
                this._appendFormParam(params, parentKey, '');
            }
            return params;
        }

        if (Array.isArray(data)) {
            data.forEach((item, index) => {
                if (item && typeof item === 'object') {
                    this._buildFormParams(item, params, parentKey ? `${parentKey}[${index}]` : String(index));
                } else if (parentKey) {
                    this._appendFormParam(params, `${parentKey}[]`, item);
                }
            });
            return params;
        }

        if (typeof data === 'object') {
            Object.keys(data).forEach((key) => {
                const value = data[key];
                const nextKey = parentKey ? `${parentKey}[${key}]` : key;
                this._buildFormParams(value, params, nextKey);
            });
            return params;
        }

        if (parentKey) {
            this._appendFormParam(params, parentKey, data);
        }
        return params;
    }
}

export default new ApiClient();
